/**
 * Parser for the canonical GLM flow-record message produced by the backend's
 * `format_record_message` (py/services/kilo_models/.../glm_record_message.py):
 *
 *   kind={kind} | action={action} | trigger={trigger} | gym={gym_id} | athlete={athlete_id or 'none'} | detail={detail}
 *
 * The records modal parses this into structured fields rather than showing the raw
 * pipe-delimited string. `detail` may itself contain '=' and (rarely) ' | ', so
 * everything from the `detail=` segment onward is treated as the detail value.
 *
 * Composite actions (link_contact_and_add_tag, link_contact_and_move_pipeline_stage,
 * link_athlete_and_set_status) may one day encode per-sub-step outcomes in `detail`
 * as "step: result / step: result". This parser surfaces those when present and
 * otherwise falls back to the plain detail string (forward-compatible — the backend
 * does not emit sub-steps today).
 */

export interface RecordSubStep {
  step: string;
  result: string;
}

export interface ParsedRecordMessage {
  kind: string | null;
  action: string | null;
  trigger: string | null;
  gymId: string | null;
  athleteId: string | null;
  detail: string | null;
  subSteps: RecordSubStep[] | null;
  raw: string;
}

const DETAIL_PREFIX = 'detail=';

// Result tokens that mark a "step: result" segment as a genuine sub-step outcome
// (guards against treating prose like "Error: contact not found" as a sub-step).
const SUB_STEP_RESULTS = new Set([
  'success',
  'failed',
  'failure',
  'no_op',
  'no-op',
  'noop',
  'skipped',
  'ok',
  'error',
]);

export function parseRecordSubSteps(detail: string | null): RecordSubStep[] | null {
  if (!detail || !detail.includes(':')) return null;
  const segments = detail
    .split(' / ')
    .map(segment => segment.trim())
    .filter(Boolean);
  if (segments.length === 0) return null;

  const steps: RecordSubStep[] = [];
  for (const segment of segments) {
    const colon = segment.indexOf(':');
    if (colon === -1) return null;
    const step = segment.slice(0, colon).trim();
    const result = segment.slice(colon + 1).trim();
    if (!step || !result) return null;
    steps.push({ step, result });
  }

  const hasKnownResult = steps.some(step => SUB_STEP_RESULTS.has(step.result.toLowerCase()));
  return hasKnownResult ? steps : null;
}

export function parseRecordMessage(message: string | null): ParsedRecordMessage | null {
  if (!message) return null;

  const parts = message.split(' | ');
  // Only treat as canonical when it opens with the `kind=` field.
  if (!parts[0]?.startsWith('kind=')) {
    return {
      kind: null,
      action: null,
      trigger: null,
      gymId: null,
      athleteId: null,
      detail: message,
      subSteps: parseRecordSubSteps(message),
      raw: message,
    };
  }

  const fields: Record<string, string> = {};
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (part.startsWith(DETAIL_PREFIX)) {
      // Everything from here on belongs to detail (may contain ' | ' / '=').
      fields.detail = parts.slice(i).join(' | ').slice(DETAIL_PREFIX.length);
      break;
    }
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    const key = part.slice(0, eq);
    if (!(key in fields)) fields[key] = part.slice(eq + 1);
  }

  const athlete = fields.athlete && fields.athlete !== 'none' ? fields.athlete : null;
  const detail = fields.detail && fields.detail.length ? fields.detail : null;

  return {
    kind: fields.kind ?? null,
    action: fields.action ?? null,
    trigger: fields.trigger ?? null,
    gymId: fields.gym ?? null,
    athleteId: athlete,
    detail,
    subSteps: parseRecordSubSteps(detail),
    raw: message,
  };
}
